import { text } from "~/assets/js/theme/constants";
import moment from "moment";
import { isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";
import i18n from "./i18n";

const bbCodes = [
    {
        regex: /(.*?)(?=$)/gm,
        func: (full, content) =>
            content.length > 0 ? `<p style="margin: 0">${content}</p>` : "",
    },
    {
        regex: /\[strong\](.*?)\[\/strong\]/gm,
        func: (full, content) =>
            `<span style="font-weight: ${text.BOLD.WEIGHT}">${content}</span>`,
    },
    {
        regex: /\[b\](.*?)\[\/b\]/gm,
        func: (full, content) =>
            `<span style="font-weight: ${text.BOLD.WEIGHT}">${content}</span>`,
    },
];

/**
 * Use together with dangerouslySetInnerHtml - never use with user-generated content
 * @param text
 * @returns a html _string_ instead of BB-Codes
 */
export const applyBbCodes = (text) => {
    let outText = text;
    for (const bb of bbCodes) {
        outText = outText.replaceAll(bb.regex, bb.func);
    }
    return outText;
};

/**
 * Waits a certain amount
 * @param ms
 */
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * returns an uuid4 string;
 * @returns {string}
 */
export const generateUuid = () => {
    return uuidv4();
};

/**
 * Formats a float into a readable Euro price
 * @param value
 * @returns {string}
 */
export const formatPrice = (value) =>
    new Intl.NumberFormat("DE-de", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);

/**
 * Just replaces the dot with a comma
 * @param value
 */
export const formatFloat = (value) =>
    ("" + Math.round(value * 100) / 100).replace(".", ",");

/**
 * Takes an unformatted string and a mapping table. If the string is not in the mapping table, it returns a default beautification of the string
 * @param {string} needle something-like-this will become "Something Like This"
 * @param haystack
 * @returns {{[p: string]: *}|{name: string, description: string, id}}
 */
export const easyTranslate = (needle, haystack) => {
    if (haystack[needle]) return { id: needle, ...haystack[needle] };
    return {
        name: capitalize(
            needle
                .replaceAll("-", " ")
                .replaceAll("ue", "ü")
                .replaceAll("ae", "ä")
                .replaceAll("oe", "ö")
        ),
        description: "",
        id: needle,
    };
};

export const createDescription = (category, amount = 0, isRamen = false) => {
    return amount > 0
        ? i18n.t(`configurator.ingredientMainCategories.description`, {
              count: amount,
              product: i18n.t(
                  "configurator.ingredientMainCategories." +
                      category +
                      ".description",
                  {
                      count: amount,
                      context: isRamen ? "sauce" : "dressing",
                  }
              ),
              context: category,
              defaultValue: null,
          })
        : null;
};

/**
 * Capitalizes first letters of words in string.
 * @param {string} str String to be modified
 * @param {boolean=false} lower Whether all other letters should be lowercased
 * @return {string}
 * @usage
 *   capitalize('fix this string');     // -> 'Fix This String'
 *   capitalize('javaSCrIPT');          // -> 'JavaSCrIPT'
 *   capitalize('javaSCrIPT', true);    // -> 'Javascript'
 */
export const capitalize = (str, lower = false) =>
    (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) =>
        match.toUpperCase()
    );

/**
 * Calculates the calories of an ingredient based on its weight
 * @param ingredient
 * @returns {number}
 */
export const getTotalEnergy = (ingredient) => {
    if (!ingredient.details.weight || !ingredient.details.energy) return 0;
    return (ingredient.details.energy / 100) * ingredient.details.weight;
};

/**
 * Groupes given set of cart items to a format of
 * [
 *  {key: [productId, ...ingredientsIds]}
 * ]
 * also returns the set of all unique ids
 *
 * @returns { groupedData: <format-above>[], iDs: <number>[] }
 */
export const groupProductAndIngredientsByItem = (items) => {
    const groupedData = [];
    const iDs = new Set();
    items.map((item) => {
        let itemIds = [item.product_id];
        const ingredients = item.add;
        if (ingredients && ingredients.length > 0) {
            const ingredientIds = ingredients.map((ing) => ing.product_id);
            itemIds = [...itemIds, ...ingredientIds];
        }
        itemIds.forEach((id) => iDs.add(id));
        const productPackage = {
            key: item.key,
            items: [...itemIds],
        };
        groupedData.push(productPackage);
    });
    return {
        groupedData,
        iDs: [...iDs],
    };
};

export const computeOutOfStockItems = (packages, stocks) => {
    let outOfStockItems = new Set();
    packages.map((productPackage) => {
        const outOfStockItem = productPackage.items.some((item) => {
            const oos = stocks.find((s) => {
                const hasMatchingId =
                    item === s.wordpress_id || item === s?.wordpress_alt_id;
                return hasMatchingId && !s.in_stock;
            });
            return !!oos;
        });
        if (outOfStockItem) outOfStockItems.add(productPackage.key);
    });
    return [...outOfStockItems];
};

/**
 * Converts past order products to cart items data model
 * Filters for only products and removes water and Co2 compensation
 * @param {array} items
 * @returns {array} products
 */
export const convertOrderToCartItems = (items) => {
    const POTT_COMPENSATION_PRODUCTS = [
        reactConfig.waterProductId,
        reactConfig.co2ProductId,
    ];

    return items
        .filter(
            (i) =>
                i.type === "product" &&
                !POTT_COMPENSATION_PRODUCTS.includes(i.wordpress_product_id)
        )
        .map((p, idx) => {
            return {
                id: p.wordpress_product_id,
                key: idx,
                count: p.count,
                extensions: {
                    pottsalat: { ingredients: p.meta?.ingredients },
                },
                method: p?.ingredient_type || "basic",
            };
        });
};

/**
 * Returns url params
 *
 *
 * @param {string} urlParam
 * @returns {string}
 */
export const getUrlQueryParam = (urlParam) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get(urlParam);
};
/**
 * Ensures that all products are currently
 * in WP store (are not put in draft mode)
 *
 * input parameter is result of
 * @see convertOrderToCartItems
 *
 * @param {array} products
 * @returns {boolean}
 */
export const ensureNoProductIsMissing = (productIds) => {
    const { products: WP_PRODUCTS } = reactConfig;
    const WP_INGREDIENTS = reactAvailableIngredients || [];
    const storeProductsIds = WP_PRODUCTS.map((p) => p.id);
    const storeIngredientsIds = WP_INGREDIENTS.map((i) => i.id);
    const storeItemIds = [...storeIngredientsIds, ...storeProductsIds];
    return productIds.every((productId) => storeItemIds.includes(productId));
};

/**
 * @param {array} productIds
 * @returns {array} WP available products
 */
export const filterWPAvailableProducts = (productIds) => {
    const { products: WP_PRODUCTS } = reactConfig;
    const storeProductsIds = WP_PRODUCTS.map((p) => p.id);
    return productIds.filter((productId) =>
        storeProductsIds.includes(productId)
    );
};

/**
 * Ensure that contact data is not missing from order
 * @param orderAddress
 * @returns {boolean}
 */
export const ensureNoContactDataIsMissing = (address) => {
    // This data must not be empty!
    const IMPORTANT_CONTACT_DATA = [
        "first_name",
        "last_name",
        "phone",
        "email",
    ];
    const hasAllData = Object.keys(address).every((key) => {
        if (IMPORTANT_CONTACT_DATA.includes(key)) {
            const value = address[key];
            return value !== null && value !== "";
        }
        return true;
    });
    return hasAllData;
};

/**
 * Calculate price based on store ID
 * @param storeId
 * @param product
 * @returns {number}
 */
export const calculateFranchisePrice = (storeId, product) => {
    const localPrices = product.details?.local_prices
        ? product.details.local_prices
        : product.local_prices;
    let price = 0;
    const noLocalPrices =
        isEmpty(localPrices) || localPrices[storeId] === undefined;

    if (noLocalPrices) {
        price = product.priceGross ? product.priceGross : product.price;
    } else {
        price = localPrices[storeId];
    }

    return price;
};

/**
 * As arguments receives a available opening times array
 * and returns a current opening timeslot
 * @param openingTimes
 */
export const getOpeningTimesObject = (openingTimes) => {
    const currentTime = moment();
    const currentDay = currentTime.format("dddd").toLocaleLowerCase();
    // Your moment at midnight
    const mmtMidnight = currentTime.clone().startOf("day");
    // Difference in seconds
    const diffSeconds = currentTime.diff(mmtMidnight, "seconds");
    const dailyWorkhours = openingTimes[currentDay];
    return dailyWorkhours?.find(
        (workhour) =>
            workhour.begin <= diffSeconds && workhour.end >= diffSeconds
    );
};

const KILO = 1000;
export const formatCalculatedEnergy = (energy) => Math.floor(energy / KILO);

export const encodeString = (text) => {
    return window.btoa(unescape(encodeURIComponent(text)));
};

export const decodeString = (text) => {
    return decodeURIComponent(escape(window.atob(text)));
};

export const getLocalPrice = (storeId = 0, product) => {
    return (
        product.local_prices?.find(
            (localPriceSet) => localPriceSet.store == storeId
        )?.price ?? product.price
    );
};